import { dispatchDisappearMessage } from '../../Actions/DisappearActions';
import { ensureKeptIndex, selectKeepState } from '../../slice/keptMessagesSlice';
import { Store } from '../../store';
import { CHAT_TYPE_GROUP, CHAT_TYPE_SINGLE } from './Constant';
import { parseDisappearAtMs } from './disappearTime';
import { formatUserIdToJid } from './User';

// JS setTimeout overflows above ~24.8 days (2^31 - 1 ms).
// For longer delays we re-schedule recursively after this cap.
const MAX_TIMEOUT_MS = 2147483647;

// The option list is shared; re-exported so existing importers keep working.
export { optionToMsgDisappear, msgDisappearToOption } from './disappearOptions';

// msgId → timeoutId — allows cancellation if a message is deleted before expiry
const scheduleMap = new Map();

// ─── PERSISTENT EXPIRED-MESSAGE TOMBSTONE ────────────────────────────────────
// When a message is removed by the local disappear timer we add its msgId here.
// The set is initialised from localStorage so it survives page refreshes.
// syncMessageBatch consults this set so server re-syncs can never resurrect an
// already-expired message (the server still holds it and returns it without the
// disappear_at field, which would bypass the normal date-based filter).
const EXPIRED_STORE_KEY = '_daz_expired_msg_ids';
const MAX_TOMBSTONE_SIZE = 2000; // guard against unbounded localStorage growth

const _expiredMsgIds = (() => {
    try {
        const raw = localStorage.getItem(EXPIRED_STORE_KEY);
        return new Set(raw ? JSON.parse(raw) : []);
    } catch (_) {
        return new Set();
    }
})();

/** Mark a msgId as locally expired (persists across page refreshes). */
export const markMessageExpired = msgId => {
    if (!msgId) return;
    _expiredMsgIds.add(msgId);
    try {
        let arr = Array.from(_expiredMsgIds);
        if (arr.length > MAX_TOMBSTONE_SIZE) arr = arr.slice(-MAX_TOMBSTONE_SIZE);
        localStorage.setItem(EXPIRED_STORE_KEY, JSON.stringify(arr));
    } catch (_) {}
};

/** Returns true if this msgId was previously expired by the local disappear timer. */
export const isMessageExpiredLocally = msgId => msgId && _expiredMsgIds.has(msgId);
// ─────────────────────────────────────────────────────────────────────────────

// How long to wait before asking the server again when the keep state is unknown.
const KEEP_CHECK_RETRY_MS = 60000;

const conversationJidOf = (message, chatId) => {
    const isGroup =
        message.chatType === CHAT_TYPE_GROUP ||
        (Store.getState().groupsData?.data || []).some(group => group.groupId === chatId);
    return formatUserIdToJid(chatId, isGroup ? CHAT_TYPE_GROUP : CHAT_TYPE_SINGLE);
};

// True when kept, false when not, null when the server could not be asked.
const resolveKeptBeforeExpiry = async (message, chatId) => {
    const keepState = selectKeepState(Store.getState(), message);
    if (keepState) return keepState.isKept;
    const isIndexed = await Store.dispatch(ensureKeptIndex(conversationJidOf(message, chatId)));
    if (!isIndexed) return null;
    return Store.getState().keptMessages.statusByMsgId[message.msgId]?.isKept === true;
};

// A kept message outlives its timer; an unknown keep state never deletes.
const expireUnlessKept = async (message, chatId) => {
    const isKept = await resolveKeptBeforeExpiry(message, chatId);
    if (isKept === false) {
        dispatchDisappearMessage(message, chatId);
        return;
    }
    if (isKept === null) {
        const id = setTimeout(() => {
            scheduleMap.delete(message.msgId);
            expireUnlessKept(message, chatId);
        }, KEEP_CHECK_RETRY_MS);
        scheduleMap.set(message.msgId, id);
    }
};

/**
 * Schedule (or immediately dispatch) expiry for a single message.
 *
 * @param {object} message  Full message object (must have msgId and disappear_at)
 * @param {string} chatId   Chat history key (contact userId or groupId)
 */
export const scheduleMessageExpiry = (message, chatId) => {
    const msgId = message.msgId;
    // Normalize camelCase (SDK receive path) to snake_case used throughout
    const disappear_at = message.disappear_at || message.disappearAt;
    if (!msgId || !disappear_at || !chatId) return;

    // Work with a normalized copy so recursive calls and dispatchDisappearMessage
    // always see disappear_at (snake_case)
    const msg = disappear_at === message.disappear_at ? message : { ...message, disappear_at };

    const expiryMs = parseDisappearAtMs(disappear_at);
    if (isNaN(expiryMs)) return;

    // Cancel any pre-existing schedule for this message (idempotent re-scheduling)
    cancelSchedule(msgId);

    const delay = expiryMs - Date.now();

    if (delay <= 0) {
        // Already expired — defer to next tick so we never dispatch inside a render cycle
        const id = setTimeout(() => {
            scheduleMap.delete(msgId);
            expireUnlessKept(msg, chatId);
        }, 0);
        scheduleMap.set(msgId, id);
        return;
    }

    // Clamp to JS max; if message is >24.8 days away, re-schedule recursively
    const clampedDelay = Math.min(delay, MAX_TIMEOUT_MS);
    const id = setTimeout(() => {
        scheduleMap.delete(msgId);
        const remaining = expiryMs - Date.now();
        if (remaining > 0) {
            scheduleMessageExpiry(msg, chatId);
        } else {
            expireUnlessKept(msg, chatId);
        }
    }, clampedDelay);

    scheduleMap.set(msgId, id);
};

/**
 * Cancel a pending expiry timer for a message (e.g. when the message is manually deleted).
 */
export const cancelSchedule = msgId => {
    if (scheduleMap.has(msgId)) {
        clearTimeout(scheduleMap.get(msgId));
        scheduleMap.delete(msgId);
    }
};

/**
 * Scan the entire chatConversationHistory Redux state and schedule / immediately
 * expire all messages that carry a disappear_at field.
 * Also scans recent chat last-messages for chats not yet opened (no full history loaded).
 * Call once on app startup after Redux is hydrated.
 */
export const runStartupCleanup = () => {
    const state = Store.getState();

    // Full chat history — covers all opened chats
    const chatHistory = state.chatConversationHistory?.data || {};
    Object.entries(chatHistory).forEach(([chatId, chatData]) => {
        const messages = chatData?.messages || {};
        Object.values(messages).forEach(message => {
            if (message.disappear_at || message.disappearAt) {
                scheduleMessageExpiry(message, chatId);
            }
        });
    });

    // Recent chat last-messages — covers chats not yet opened (history not in Redux/IDB)
    const recentChats = state.recentChatData?.data || [];
    recentChats.forEach(recentChat => {
        // SDK uses disappearAt (camelCase) in recent chat objects; normalize to disappear_at
        const disappearAt = recentChat.disappear_at || recentChat.disappearAt;
        if (disappearAt && recentChat.msgId && recentChat.fromUserId) {
            scheduleMessageExpiry({ ...recentChat, disappear_at: disappearAt }, recentChat.fromUserId);
        }
    });
};
